/**
 * IssueFeedbackPage — /feedback/:rating?t=<token>
 *
 * Landing target for the one-click rating block ({{issueFeedback}}) at the foot
 * of a campaign email — The Weekly Close first (mct_de118f31-c35).
 *
 * The email link is a plain GET that only opens this page, so mail scanners
 * pre-fetching it record nothing. The rating is written by THIS page's own POST
 * once it is actually on screen (same pattern as CallAttendPage). Picking a
 * different option here, or sending a note, is a deliberate act and marks the
 * reader as a person (see worker/src/issue-feedback.ts for the scanner rules).
 *
 * The page then shows how other readers rated the issue, asks whether the
 * reader wants to say more (one question if yes), and continues to the
 * issue's web page.
 */

import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import TakerShell from './components/TakerShell';

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';

type Rating = 'awesome' | 'good' | 'not_useful';
const RATINGS: Rating[] = ['awesome', 'good', 'not_useful'];

interface PageData {
  rating: Rating;
  issueTitle: string;
  brandName: string | null;
  continueUrl: string | null;
  options: Array<{ key: Rating; label: string }>;
  tallies: { total: number; counts: Record<Rating, number>; percents: Record<Rating, number> };
  preview: boolean;
  followUp: { liked: string | null; moreOf: string | null; lessOf: string | null; comment: string | null } | null;
}

type PageState =
  | { phase: 'loading' }
  | { phase: 'ready'; data: PageData }
  | { phase: 'error'; message: string };

const ACCENT = '#F05A28';
const INK = '#0A0A2E';
const MUTED = '#6b7280';

/** Resolve once the page is actually in front of someone. A background-tab
 *  prefetch never records. */
function whenVisible(): Promise<void> {
  if (typeof document === 'undefined' || document.visibilityState === 'visible') return Promise.resolve();
  return new Promise((resolve) => {
    const on = () => {
      if (document.visibilityState === 'visible') {
        document.removeEventListener('visibilitychange', on);
        resolve();
      }
    };
    document.addEventListener('visibilitychange', on);
  });
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const r = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const d = (await r.json().catch(() => ({}))) as T & { ok?: boolean; error?: string };
  if (!r.ok || !d.ok) throw new Error(d.error || 'Something went wrong — please try the link again.');
  return d;
}

export default function IssueFeedbackPage() {
  const { rating: ratingParam } = useParams<{ rating: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') ?? '';
  const [state, setState] = useState<PageState>({ phase: 'loading' });
  const [changing, setChanging] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!token || !RATINGS.includes(ratingParam as Rating)) {
      setState({ phase: 'error', message: 'This link is incomplete.' });
      return;
    }
    const webdriver = typeof navigator !== 'undefined' && (navigator as Navigator & { webdriver?: boolean }).webdriver === true;
    whenVisible()
      .then(() => postJson<PageData>('/api/public/issue-feedback/vote', { t: token, rating: ratingParam, source: 'link', webdriver }))
      .then((data) => setState({ phase: 'ready', data }))
      .catch((err: Error) => setState({ phase: 'error', message: err.message }));
  }, [token, ratingParam]);

  async function change(next: Rating) {
    if (state.phase !== 'ready' || next === state.data.rating || changing) return;
    setChanging(true);
    try {
      const data = await postJson<PageData>('/api/public/issue-feedback/vote', { t: token, rating: next, source: 'page' });
      setState({ phase: 'ready', data });
    } catch (err) {
      setState({ phase: 'error', message: (err as Error).message });
    } finally {
      setChanging(false);
    }
  }

  return (
    <TakerShell>
      <style>{CSS}</style>
      <div className="ifb-wrap">
        {state.phase === 'loading' && <div className="ifb-muted">One moment…</div>}

        {state.phase === 'error' && (
          <div className="ifb-card">
            <h1 className="ifb-h1">That link didn't work</h1>
            <p className="ifb-muted">{state.message}</p>
          </div>
        )}

        {state.phase === 'ready' && (
          <ReadyView data={state.data} token={token} changing={changing} onChange={change} />
        )}
      </div>
    </TakerShell>
  );
}

function ReadyView({ data, token, changing, onChange }: {
  data: PageData; token: string; changing: boolean; onChange: (r: Rating) => void;
}) {
  return (
    <div className="ifb-card">
      {data.preview && (
        <div className="ifb-preview">Preview copy: this page works, but nothing you choose here is recorded.</div>
      )}
      <div className="ifb-check" aria-hidden>✓</div>
      <h1 className="ifb-h1">{data.preview ? 'This is what a reader sees' : 'Your response has been recorded'}</h1>
      <p className="ifb-muted ifb-issue">{data.issueTitle}</p>

      <div className="ifb-label">How readers rated this issue</div>
      <div className="ifb-bars" role="list">
        {data.options.map((o) => {
          const pct = data.tallies.percents[o.key] ?? 0;
          const mine = o.key === data.rating;
          return (
            <button
              key={o.key}
              type="button"
              role="listitem"
              className={`ifb-bar${mine ? ' ifb-bar--mine' : ''}`}
              onClick={() => onChange(o.key)}
              disabled={changing}
              aria-pressed={mine}
              title={mine ? 'Your choice' : `Change my answer to "${o.label}"`}
            >
              <span className="ifb-bar-fill" style={{ width: `${pct}%` }} />
              <span className="ifb-bar-text">
                <span>{o.label}{mine && <span className="ifb-you"> · your choice</span>}</span>
                <span className="ifb-pct">{pct}%</span>
              </span>
            </button>
          );
        })}
      </div>
      <p className="ifb-small">
        {data.tallies.total === 1 ? 'You are the first reader to rate this one.' : `${data.tallies.total.toLocaleString()} readers so far.`}
        {' '}Changed your mind? Pick another answer above.
      </p>

      <FollowUpForm token={token} initial={data.followUp} preview={data.preview} continueUrl={data.continueUrl} />
    </div>
  );
}

/**
 * The optional note, collapsed behind two buttons (Michael, mca_0234bc38dbed):
 * "Yes" opens ONE question, "What would you like more of?" (Lisa's feedback);
 * "No, continue to the issue" goes straight on. The POST shape is unchanged,
 * so liked / lessOf / comment are simply sent empty and stored as NULL —
 * Command Centre's aggregate read and the Report tab keep working as-is.
 */
function FollowUpForm({ token, initial, preview, continueUrl }: {
  token: string; initial: PageData['followUp']; preview: boolean; continueUrl: string | null;
}) {
  const [mode, setMode] = useState<'ask' | 'open' | 'declined'>(initial ? 'open' : 'ask');
  const [moreOf, setMoreOf] = useState(initial?.moreOf ?? '');
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>(initial ? 'saved' : 'idle');
  const [err, setErr] = useState('');
  const empty = !moreOf.trim();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (empty) return;
    setStatus('saving');
    try {
      await postJson('/api/public/issue-feedback/follow-up', { t: token, moreOf });
      setStatus('saved');
      if (continueUrl) window.location.assign(continueUrl);
    } catch (e2) {
      setErr((e2 as Error).message);
      setStatus('error');
    }
  }

  const continueLink = continueUrl && (
    <a className="ifb-continue" href={continueUrl}>Continue to the issue →</a>
  );

  if (mode === 'ask') {
    return (
      <div className="ifb-form">
        <div className="ifb-label">Want to tell us a little more? (optional)</div>
        <div className="ifb-choice">
          <button type="button" className="ifb-yes" onClick={() => setMode('open')}>Yes</button>
          {continueUrl
            ? <a className="ifb-no" href={continueUrl}>No, continue to the issue →</a>
            : <button type="button" className="ifb-no" onClick={() => setMode('declined')}>No thanks</button>}
        </div>
      </div>
    );
  }

  if (mode === 'declined') return <div className="ifb-form">{continueLink}</div>;

  return (
    <form className="ifb-form" onSubmit={submit}>
      <Field
        label="What would you like more of?"
        value={moreOf}
        onChange={(v) => { setMoreOf(v); if (status === 'saved') setStatus('idle'); }}
        rows={3}
      />
      <div className="ifb-actions">
        <button type="submit" className="ifb-send" disabled={empty || status === 'saving' || status === 'saved'}>
          {status === 'saving' ? 'Sending…' : status === 'saved' ? 'Sent' : continueUrl ? 'Send and continue to the issue →' : 'Send my feedback'}
        </button>
        {status === 'saved' && (
          <span className="ifb-thanks">
            {preview ? 'Preview copy — not saved.' : 'Thank you. Every note is read by the team.'}
          </span>
        )}
        {status === 'error' && <span className="ifb-err">{err}</span>}
      </div>
      {status === 'saved' && continueLink}
    </form>
  );
}

function Field({ label, value, onChange, rows = 2 }: {
  label: string; value: string; onChange: (v: string) => void; rows?: number;
}) {
  return (
    <label className="ifb-field">
      <span>{label}</span>
      <textarea rows={rows} maxLength={2000} value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

const CSS = `
  .ifb-wrap { min-height: 60vh; display: flex; justify-content: center; padding: 40px 16px 64px; font-family: 'Inter', sans-serif; }
  .ifb-card { width: 100%; max-width: 560px; }
  .ifb-muted { color: ${MUTED}; font-size: 15px; line-height: 1.6; }
  .ifb-preview { background: #FFF7ED; border: 1px solid #FED7AA; color: #9A3412; font-size: 13px; padding: 10px 14px; border-radius: 8px; margin-bottom: 20px; }
  .ifb-check { width: 52px; height: 52px; border-radius: 50%; background: #FFE4D6; color: ${ACCENT}; display: flex; align-items: center; justify-content: center; font-size: 24px; font-weight: 700; margin-bottom: 16px; }
  .ifb-h1 { font-family: 'Fraunces', serif; font-size: 28px; font-weight: 800; color: ${INK}; margin: 0 0 6px; line-height: 1.2; }
  .ifb-issue { margin: 0 0 28px; }
  .ifb-label { font-size: 13px; font-weight: 700; color: ${INK}; text-transform: uppercase; letter-spacing: 0.6px; margin: 0 0 10px; }
  .ifb-bars { display: flex; flex-direction: column; gap: 8px; }
  .ifb-bar { position: relative; overflow: hidden; width: 100%; text-align: left; border: 1.5px solid #e5e7eb; border-radius: 8px; background: #fff; padding: 0; cursor: pointer; font: inherit; }
  .ifb-bar:disabled { cursor: progress; }
  .ifb-bar--mine { border-color: ${ACCENT}; }
  .ifb-bar-fill { position: absolute; inset: 0 auto 0 0; background: #F3F4F6; transition: width 0.4s ease; }
  .ifb-bar--mine .ifb-bar-fill { background: #FFE4D6; }
  .ifb-bar-text { position: relative; display: flex; justify-content: space-between; gap: 12px; padding: 12px 14px; font-size: 15px; font-weight: 600; color: ${INK}; }
  .ifb-you { font-weight: 500; color: ${ACCENT}; font-size: 13px; }
  .ifb-pct { font-variant-numeric: tabular-nums; }
  .ifb-small { font-size: 13px; color: ${MUTED}; margin: 10px 0 32px; }
  .ifb-form { border-top: 1px solid #e5e7eb; padding-top: 24px; }
  .ifb-field { display: block; margin-bottom: 14px; }
  .ifb-field span { display: block; font-size: 14px; font-weight: 600; color: ${INK}; margin-bottom: 6px; }
  .ifb-field textarea { width: 100%; box-sizing: border-box; border: 1.5px solid #d1d5db; border-radius: 8px; padding: 10px 12px; font: inherit; font-size: 15px; resize: vertical; }
  .ifb-field textarea:focus { outline: none; border-color: ${ACCENT}; }
  .ifb-choice { display: flex; flex-wrap: wrap; gap: 10px; }
  .ifb-yes { background: ${INK}; color: #fff; border: 0; border-radius: 8px; padding: 12px 24px; font: inherit; font-weight: 600; font-size: 15px; cursor: pointer; }
  .ifb-no { flex: 1; min-width: 220px; text-align: center; background: ${ACCENT}; color: #fff; border: 0; border-radius: 8px; padding: 12px 20px; font: inherit; font-weight: 700; font-size: 15px; text-decoration: none; cursor: pointer; }
  .ifb-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; }
  .ifb-send { background: ${INK}; color: #fff; border: 0; border-radius: 8px; padding: 11px 20px; font: inherit; font-weight: 600; font-size: 15px; cursor: pointer; }
  .ifb-send:disabled { opacity: 0.55; cursor: default; }
  .ifb-thanks { font-size: 14px; color: #166534; }
  .ifb-err { font-size: 14px; color: #991b1b; }
  .ifb-continue { display: block; text-align: center; margin-top: 32px; background: ${ACCENT}; color: #fff; text-decoration: none; font-weight: 700; font-size: 16px; padding: 14px 20px; border-radius: 8px; }
`;
